import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Deal } from "@/core/entities/deal";
import { getStagesByCategory } from "@/utils/dealStage";
import { formatDuration, timestampToDate } from "@/utils/formatting";
import { format } from "date-fns";
import { Check } from "lucide-react";
import { getStageDurationText } from "../utils/deal-dialog-utils";

// Stage Duration Pills Component
interface StageDurationPillsProps {
  deal: Deal;
}

export const StageDurationPills = ({ deal }: StageDurationPillsProps) => {
  const stageHistory = deal.stageHistory;

  // Determine deal category based on deal.type or deal.buyOrSell
  const dealCategory =
    deal.type === "Purchase" || deal.buyOrSell?.toLowerCase() === "sell"
      ? "sell"
      : "buy";

  // Get stages for the specific deal category
  const relevantStages = getStagesByCategory(dealCategory);
  const currentStage = stageHistory?.currentStage;
  return (
    <TooltipProvider>
      <div className="flex items-center gap-2 flex-wrap">
        {relevantStages.map((stageDefinition, stageIndex) => {
          const stageValue = Number(stageDefinition.value);
          console.log("Stage Value:", stageValue);
          const stageDuration = stageHistory?.stageDurations.find(
            (sd) => sd.stage === stageValue,
          );
          console.log("Stage Duration:", stageDuration);
          const isCompleted = stageDuration && stageDuration.exitedAt;
          const isCurrent = stageValue === currentStage;

          // Find current stage index in relevantStages
          const currentStageIndex = relevantStages.findIndex(
            (s) => Number(s.value) === currentStage,
          );
          const isCurrentOrPrevious = stageIndex <= currentStageIndex;
          const isFirst = stageIndex === 0;
          const isLast = stageIndex === relevantStages.length - 1;

          let pillClass = "";
          let borderClass = "";
          let iconClass = "";

          if (isCurrentOrPrevious) {
            // Current and previous stages are blue
            pillClass =
              "bg-blue-600 text-white dark:bg-blue-500 dark:text-white";
            borderClass = "border-blue-600 dark:border-blue-500";
            iconClass = "text-white dark:text-white";
          } else {
            // Future stages are slate
            pillClass =
              "bg-slate-200 text-slate-700 dark:bg-slate-900 dark:text-slate-300";
            borderClass = "border-slate-300 dark:border-blue-500";
            iconClass = "text-slate-700 dark:text-slate-300";
          }

          // Add border radius only to first and last pills
          let radiusClass = "";
          if (isFirst && isLast) {
            radiusClass = "rounded-full";
          } else if (isFirst) {
            radiusClass = "rounded-l-full";
          } else if (isLast) {
            radiusClass = "rounded-r-full";
          }

          const tooltipContent = (
            <div className="text-sm space-y-1">
              <div className="font-semibold text-gray-900 dark:text-white">
                {stageDefinition.displayName.split(" ").slice(1).join(" ")}
              </div>
              {!isCurrent && isCurrentOrPrevious && (
                <div className="text-gray-600 dark:text-gray-300">
                  Completed
                </div>
              )}
              {isCurrent && (
                <>
                  <div className="text-gray-600 dark:text-gray-300">
                    Currently in this stage
                  </div>
                  <div className="text-gray-600 dark:text-gray-300">
                    For {getStageDurationText(deal)}
                  </div>
                </>
              )}
              {!isCurrent && !isCurrentOrPrevious && (
                <div className="text-gray-600 dark:text-gray-300">
                  Not yet reached
                </div>
              )}

              {stageDuration && (
                <>
                  <div className="text-gray-600 dark:text-gray-300">
                    Duration: {formatDuration(stageDuration.durationMs)}
                  </div>
                  {stageDuration.enteredAt &&
                    timestampToDate(stageDuration.enteredAt) && (
                      <div className="text-gray-600 dark:text-gray-300">
                        Entered:{" "}
                        {format(
                          timestampToDate(stageDuration.enteredAt)!,
                          "MMM d, yyyy 'at' h:mm a",
                        )}
                      </div>
                    )}
                  {stageDuration.exitedAt &&
                    timestampToDate(stageDuration.exitedAt) && (
                      <div className="text-gray-600 dark:text-gray-300">
                        Exited:{" "}
                        {format(
                          timestampToDate(stageDuration.exitedAt)!,
                          "MMM d, yyyy 'at' h:mm a",
                        )}
                      </div>
                    )}
                </>
              )}
            </div>
          );

          return (
            <div key={stageDefinition.key} className="flex items-center">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={`relative inline-flex items-center justify-center px-6 py-2 min-w-[4rem] h-8 border-2 cursor-pointer transition-all duration-200 hover:scale-105 ${pillClass} ${borderClass} ${radiusClass}`}
                  >
                    {(isCompleted || isCurrent || isCurrentOrPrevious) && (
                      <Check className={`h-4 w-4 ${iconClass}`} />
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs rounded-none">
                  {tooltipContent}
                </TooltipContent>
              </Tooltip>
            </div>
          );
        })}
      </div>
    </TooltipProvider>
  );
};
